import { lazy, useMemo } from 'react';
import { Box, Text, Group, useMantineTheme } from '@mantine/core';
import { IconChartPie } from '@tabler/icons-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { WidgetProps, ProjectData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ProjectCompletionGraph Widget Component
// ============================================================================

function ProjectCompletionGraphWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const projects = globalData?.projects || [];

  // Group projects by status
  const statusData = useMemo(() => {
    const statusMap: Record<string, number> = {
      planning: 0,
      'in-progress': 0,
      'on-hold': 0,
      completed: 0,
      cancelled: 0,
    };

    projects.forEach((project) => {
      statusMap[project.status] = (statusMap[project.status] || 0) + 1;
    });

    const statusColors: Record<string, string> = {
      planning: theme.colors.gray[5],
      'in-progress': theme.colors.blue[5],
      'on-hold': theme.colors.yellow[5],
      completed: theme.colors.green[5],
      cancelled: theme.colors.red[5],
    };

    const statusLabels: Record<string, string> = {
      planning: 'Planning',
      'in-progress': 'In Progress',
      'on-hold': 'On Hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    return Object.entries(statusMap)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => ({
        name: statusLabels[status] || status,
        value: count,
        color: statusColors[status] || theme.colors.gray[5],
      }));
  }, [projects, theme.colors]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = projects.length;
    const completed = projects.filter((p) => p.status === 'completed').length;
    const inProgress = projects.filter((p) => p.status === 'in-progress').length;
    const avgProgress = projects.length > 0
      ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
      : 0;

    return { total, completed, inProgress, avgProgress };
  }, [projects]);

  if (projects.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconChartPie size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No project data available
          </Text>
        </Box>
      </FadeIn>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          p="xs"
          style={{
            backgroundColor: theme.white,
            border: `1px solid ${theme.colors.gray[2]}`,
            borderRadius: theme.radius.sm,
          }}
        >
          <Text size="sm" fw={500} style={accessibilityStyles}>
            {data.name}
          </Text>
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            {data.value} projects ({Math.round((data.value / stats.total) * 100)}%)
          </Text>
        </Box>
      );
    }
    return null;
  };

  return (
    <FadeIn style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Stats summary */}
        <Group justify="center" gap="lg" mb="md">
          <Box style={{ textAlign: 'center' }}>
            <Text size="xl" fw={700} c="blue" style={accessibilityStyles}>
              {stats.total}
            </Text>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>
              Total
            </Text>
          </Box>
          <Box style={{ textAlign: 'center' }}>
            <Text size="xl" fw={700} c="green" style={accessibilityStyles}>
              {stats.completed}
            </Text>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>
              Completed
            </Text>
          </Box>
          <Box style={{ textAlign: 'center' }}>
            <Text size="xl" fw={700} c="orange" style={accessibilityStyles}>
              {stats.avgProgress}%
            </Text>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>
              Avg Progress
            </Text>
          </Box>
        </Group>

        {/* Pie chart */}
        <Box style={{ flex: 1, minHeight: 100 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <Text component="span" size="xs" style={accessibilityStyles}>
                    {value}
                  </Text>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'project-completion',
  name: 'Project Completion',
  description: 'Donut chart showing project status distribution with statistics',
  icon: IconChartPie,
  category: 'charts',
  component: lazy(() => Promise.resolve({ default: ProjectCompletionGraphWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['small', 'medium', 'large', 'full'],
    defaultVariant: 'medium',
    variantSizes: {
      small: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      medium: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      large: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 8, rows: 6 },
        lg: { cols: 6, rows: 6 },
        xl: { cols: 6, rows: 6 },
        tv: { cols: 6, rows: 5 },
      },
      full: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 12, rows: 6 },
        lg: { cols: 12, rows: 6 },
        xl: { cols: 12, rows: 6 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['projects'],
  },
  hasSettings: true,
});

export default ProjectCompletionGraphWidget;
